import React, { useEffect, useState } from "react";
import { Box, Button, Stack, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";

import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";

import PageHeader from "../../components/common/PageHeader";
import DataTable from "../../components/common/DataTable";
import StatusChip from "../../components/common/StatusChip";

import { NOTIFICATION_STRINGS, COMMON_STRINGS } from "../../constants/strings";

import { getNotifications, markAsRead } from "../../services/api";

// 🔥 Redux
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

import "../table-common.css";

const NotificationPage = () => {
  const dispatch = useDispatch();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // ================= FETCH =================
  const fetchNotifications = async () => {
    try {
      setLoading(true);

      const res = await getNotifications();
      console.log(res);

      const apiData = Array.isArray(res.data) ? res.data : res.data?.data || [];

      const formatted = apiData.map((item: any) => ({
        id: item._id,
        title: item.title || "No Title",
        type: item.type?.toLowerCase() || "info",

        name: item.meta?.userId?.name || item.user?.name || "Guest User",
        mobile: item.meta?.userId?.mobile || item.user?.mobile || "",

        date: item.createdAt
          ? new Date(item.createdAt).toLocaleDateString()
          : "-",

        status: item.isRead ? "read" : "unread",
      }));

      setRows(formatted);
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // ================= MARK AS READ =================
  const handleMarkRead = async (id: string) => {
    try {
      setRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, status: "read" } : row)),
      );

      await markAsRead(id);

      dispatch(
        showSnackbar({
          message: "Marked as read",
          type: "success",
        }),
      );
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        }),
      );

      fetchNotifications();
    }
  };

  // ================= FILTER =================
  const filteredRows = rows.filter((row) => {
    const matchSearch = [row.name, row.mobile, row.title, row.type, row.status]
      .join(" ")
      .toLowerCase()
      .includes(searchText.toLowerCase());

    const matchStatus = statusFilter ? row.status === statusFilter : true;

    return matchSearch && matchStatus;
  });

  // ================= COLUMNS =================
  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "User",
      flex: 1.5,
      renderCell: (params) =>
        params.row.name?.trim() ? params.row.name : "Guest User",
    },
    {
      field: "mobile",
      headerName: "Contact No",
      flex: 1.5,
      renderCell: (params) =>
        params.row.mobile?.toString().trim() ? params.row.mobile : "-",
    },
    {
      field: "title",
      headerName: NOTIFICATION_STRINGS.COLUMN_TITLE,
      flex: 2,
    },
    {
      field: "type",
      headerName: NOTIFICATION_STRINGS.COLUMN_TYPE,
      flex: 1,
    },
    {
      field: "date",
      headerName: NOTIFICATION_STRINGS.COLUMN_DATE,
      flex: 1,
    },
    {
      field: "status",
      headerName: NOTIFICATION_STRINGS.COLUMN_STATUS,
      flex: 1,
      renderCell: (params) => <StatusChip status={params.value} />,
    },
    {
      field: "actions",
      headerName: NOTIFICATION_STRINGS.COLUMN_ACTIONS,
      flex: 1.5,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const isRead = params.row.status === "read";

        return (
          <Box className="action-cell">
            <Stack direction="row" spacing={0.5}>
              <Tooltip
                title={isRead ? "Already marked as read" : "Mark as read"}
              >
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={isRead}
                    onClick={() => handleMarkRead(params.row.id)}
                    sx={{
                      minWidth: "32px",
                      padding: "6px",
                      backgroundColor: "var(--success)",
                    }}
                  >
                    <MarkEmailReadIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <PageHeader
        title={NOTIFICATION_STRINGS.TITLE}
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder={NOTIFICATION_STRINGS.SEARCH_PLACEHOLDER}
        // ✅ FIXED HERE
        filterValue={statusFilter}
        onFilterChange={setStatusFilter}
        filterOptions={[
          { label: "All", value: "" },
          { label: "Read", value: "read" },
          { label: "Unread", value: "unread" },
        ]}
      />

      <DataTable
        rows={filteredRows}
        columns={columns}
        loading={loading}
        getRowId={(row) => row.id}
        pageSizeOptions={[10, 20, 50]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
      />
    </Box>
  );
};

export default NotificationPage;
